import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {createApiKey, fetchApiKeys, revokeApiKey} from '@/entities/account/api';
import type {MintedApiKey} from '@/entities/account/types';
import {ApiError} from '@/shared/api';
import {formatTimestamp} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Field} from '@/shared/ui/field';
import {AppInput} from '@/shared/ui/input';
import {ListRow, PagedRows} from '@/shared/ui/list-row';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * The account's own API keys — a second way to authenticate as this human, without a
 * browser and without a refresh token.
 *
 * ## Not the same thing as the company's tokens
 *
 * `/control/settings/api/tokens` (the API-bridge screen) mints tokens for DESKTOP
 * BRIDGES and TERMINALS: they authenticate a machine into one company and die with it.
 * These authenticate the ACCOUNT — a script that reads a report, an integration that
 * files people — and they outlive any one company. Two lists, two lifetimes, and
 * revoking from the wrong one is why they are named apart everywhere they appear.
 *
 * ## The secret exists exactly once
 *
 * `POST /auth/api-keys` is the only response that ever carries the key. The list route
 * gives back metadata and nothing else, so a screen that does not put the secret in
 * front of the person immediately has thrown it away, and the only recovery is minting
 * another. Hence the banner that stays up until it is dismissed by hand rather than
 * disappearing on the next render.
 *
 * The mint form asks for `service` because the server does: it names what the key is
 * for, and it is the only field that makes a list of six keys readable a year later.
 */
const styles = StyleSheet.create({
  card: {gap: spacing(3)},
  section: {gap: spacing(2)},
  row: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  grow: {flexGrow: 1, flexBasis: 140},
});

/** Whichever field the mint response used — the shape is not pinned down. */
function secretOf(minted: MintedApiKey | undefined): string | null {
  return minted?.key || minted?.api_key || null;
}

export function ApiKeysCard() {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const [minting, setMinting] = useState(false);
  const [name, setName] = useState('');
  const [service, setService] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const keys = useQuery({queryKey: ['api-keys'], queryFn: fetchApiKeys});

  const mint = useMutation({
    mutationFn: () => createApiKey({name: name.trim(), service: service.trim()}),
    onSuccess: async minted => {
      setSecret(secretOf(minted));
      setName('');
      setService('');
      setMinting(false);
      await qc.invalidateQueries({queryKey: ['api-keys']});
    },
  });

  const revoke = useMutation({
    meta: {toast: 'toast.keyRevoked'},
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: async () => {
      setConfirmId(null);
      await qc.invalidateQueries({queryKey: ['api-keys']});
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  // A revoked key stays in the list with a timestamp; it is history, not a live key,
  // and offering "revoke" on it again would be offering a no-op.
  const rows = (keys.data?.api_keys ?? []).filter(k => !k.revoked_at);

  return (
    <View style={styles.section}>
      {/* Shown once, and only where somebody asked for it. */}
      {secret ? (
        <Card padding={4} style={styles.card}>
          <Banner tone="warning" title={t('apiKeys.secretOnce')} />
          <Field label={t('apiKeys.secret')} value={secret} mono />
          <AppButton
            title={t('apiKeys.secretHide')}
            tone="neutral"
            size="sm"
            onPress={() => setSecret(null)}
            testID="api-key-secret-hide"
          />
        </Card>
      ) : null}

      {keys.isPending ? (
        <Skeleton height={110} radius={radii.lg} />
      ) : keys.isError ? (
        <Banner tone="error" title={errorOf(keys.error)} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('apiKeys.empty')} />
      ) : (
        <PagedRows
          items={rows}
          keyOf={key => key.id}
          testID="api-keys-list"
          render={key => (
            <ListRow
              icon="lock"
              title={key.name || key.service}
              subtitle={t('apiKeys.meta', {
                service: key.service || '—',
                created: formatTimestamp(key.created_at),
              })}
              value={confirmId === key.id ? t('apiKeys.confirmRevoke') : t('actions.revoke')}
              // Two taps, like every other destructive row in the app: the first arms
              // it, the second does it. A key revoked by a mis-tap is an integration
              // down until somebody notices.
              onPress={() =>
                confirmId === key.id ? revoke.mutate(key.id) : setConfirmId(key.id)
              }
              testID={`api-key-${key.id}`}
            />
          )}
        />
      )}

      {revoke.isError ? <Banner tone="error" title={errorOf(revoke.error)} /> : null}

      {minting ? (
        <Card padding={4} style={styles.card}>
          <AppInput
            label={t('apiKeys.name')}
            value={name}
            onChangeText={setName}
            placeholder={t('apiKeys.namePlaceholder')}
            testID="api-key-name"
          />
          <AppInput
            label={t('apiKeys.service')}
            value={service}
            onChangeText={setService}
            placeholder={t('apiKeys.servicePlaceholder')}
            autoCapitalize="none"
            testID="api-key-service"
          />
          <AppText variant="caption">{t('apiKeys.serviceHint')}</AppText>
          {mint.isError ? <Banner tone="error" title={errorOf(mint.error)} /> : null}
          <View style={styles.row}>
            <AppButton
              title={t('apiKeys.mint')}
              tone="soft"
              size="sm"
              disabled={name.trim().length === 0 || service.trim().length === 0}
              loading={mint.isPending}
              onPress={() => mint.mutate()}
              style={styles.grow}
              testID="api-key-mint"
            />
            <AppButton
              title={t('actions.cancel')}
              tone="ghost"
              size="sm"
              onPress={() => {
                setMinting(false);
                mint.reset();
              }}
              style={styles.grow}
            />
          </View>
        </Card>
      ) : (
        <AppButton
          title={t('apiKeys.new')}
          tone="neutral"
          size="sm"
          onPress={() => setMinting(true)}
          testID="api-key-new"
        />
      )}
    </View>
  );
}
